import type { ReactNode } from "react";

interface LoadingProps {
    /**
     * The loading message text.
     */
    message?: string;
    /**
     * The loading overlay type.
     */
    type?: string;
    /**
     * The confirm button handler.
     */
    onConfirm?: () => void;
    /**
     * The deny button handler.
     */
    onDeny?: () => void;
    /**
     * Additional content to render.
     */
    children?: ReactNode;
    /**
     * The confirm button label.
     */
    confirmText?: string;
    /**
     * The deny button label.
     */
    denyText?: string;
    /**
     * Whether the confirm button is disabled.
     */
    confirmDisabled?: boolean;
}

/**
 * Loading spinner/dialog component with optional confirm and deny buttons.
 *
 * @param {LoadingProps} props - The component props.
 * @returns {ReactNode} The rendered loading component.
 */
const Loading = ({
    message,
    type,
    onConfirm,
    onDeny,
    children,
    confirmText,
    denyText,
    confirmDisabled,
}: LoadingProps): ReactNode => {
    const onConfirmClick = () => {
        if (onConfirm) onConfirm();
    };

    const onDenyClick = () => {
        if (onDeny) onDeny();
    };

    switch (type) {
        case "dialog":
            return (
                <div>
                    <div className="dialog-container">
                        <div className="dialog-box">
                            <div className="dialog-message">
                                {message}
                                {children}
                            </div>
                            <div
                                className="dialog-footer"
                                style={{ gap: "8px" }}
                            >
                                <button
                                    onClick={onConfirmClick}
                                    className="btn btn-primary"
                                    disabled={confirmDisabled}
                                >
                                    {confirmText ?? "Yes"}
                                </button>
                                <button
                                    onClick={onDenyClick}
                                    className="btn btn-primary"
                                >
                                    {denyText ?? "No"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        default:
            return (
                <div>
                    <div className="spinner-container">
                        <div className="spinner-box">
                            <div className="spinner" />
                            {message && (
                                <div className="spinner-message">
                                    {message}
                                    {children}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="spinner-backdrop" />
                </div>
            );
    }
};

export default Loading;
